import React, { useCallback, useEffect, useRef, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { Lightbulb, RefreshCw, Check, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { fetchSuggestions, markGotIt, clearGotIt } from "../lib/suggestions";
import SuggestionCard from "./SuggestionCard";
import { pairingCards, pairingDisplayName, pairingEmptyCopy, pairingWindowText } from "../lib/suggestionPresentation";

/**
 * Right-side drawer that shows up to 3 Suggestion cards for a
 * batch one through seven days before expiration. Only specific product
 * combinations are displayed; legacy generic and policy cards are ignored.
 *
 * Strict UI rules (per stakeholder lock):
 *   - Title is exactly "Suggestions". No AI/GPT/LLM/Freshness wording.
 *   - Cards are entirely SERVER-RENDERED; this component does not
 *     synthesise any user-facing strings beyond loading / empty state
 *     copy.
 *   - "Got it" persists for 24h in localStorage, scoped per batch.
 *
 * Props
 *   open       : boolean, controls the sheet
 *   onOpenChange(boolean): close callback
 *   batch      : { id, product_name, expiration_date, ... }
 */
export function SuggestionsDrawer({ open, onOpenChange, batch }) {
  const [result, setResult] = useState(null);
  const requestGeneration = useRef(0);
  const batchId = batch?.id;

  const load = useCallback(async ({ forceRefresh = false } = {}) => {
    if (!open || !batchId) return;
    const generation = ++requestGeneration.current;
    setResult({ batchId, loading: true, error: null, data: null });
    try {
      const payload = await fetchSuggestions(batchId, { forceRefresh });
      if (generation !== requestGeneration.current) return;
      if (payload && payload.batch_id !== batchId) {
        throw new Error("Could not verify pairings for this item. Please try again.");
      }
      setResult({ batchId, loading: false, error: null, data: payload });
    } catch (err) {
      if (generation !== requestGeneration.current) return;
      setResult({ batchId, loading: false, error: err?.message || "Could not load suggestions", data: null });
    }
  }, [batchId, open]);

  // Load when the drawer opens for a fresh batch.
  useEffect(() => {
    if (open && batchId) {
      load();
    } else {
      setResult(null);
    }
    return () => { requestGeneration.current += 1; };
  }, [open, batchId, load]);

  // Hide the prior batch synchronously, before the new request effect runs.
  const current = open && result?.batchId === batchId ? result : null;
  const loading = Boolean(open && batchId && (!current || current.loading));
  const error = current?.error || null;
  const data = current?.data || null;

  const handleGotIt = () => {
    if (batch?.id) {
      markGotIt(batch.id);
      toast.success("Got it — hidden for 24h.");
    }
    onOpenChange(false);
  };

  const handleRefresh = () => {
    if (batch?.id) clearGotIt(batch.id);
    load({ forceRefresh: true });
  };

  const cards = pairingCards(data);
  const displayName = pairingDisplayName(data, batch);
  const windowText = pairingWindowText(data);
  const emptyCopy = pairingEmptyCopy(data);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md flex flex-col p-0 bg-paper border-l border-ink/15"
        data-testid="suggestions-drawer"
      >
        <SheetHeader className="px-5 pt-5 pb-3 border-b border-border space-y-2">
          <SheetTitle
            className="flex items-center gap-2 font-display font-black text-xl tracking-tight text-ink"
            data-testid="suggestions-drawer-title"
          >
            <Lightbulb size={18} className="text-shelf-attention" />
            Suggestions
          </SheetTitle>
          <SheetDescription
            className="font-mono text-[11px] uppercase tracking-wider text-ink/60"
            data-testid="suggestions-drawer-subtitle"
          >
            For {displayName}
            {windowText && (
              <span className="block mt-1" data-testid="suggestions-days-to-expiry">
                {windowText}
              </span>
            )}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3" data-testid="suggestions-drawer-body">
          {loading && (
            <div
              className="flex items-center gap-2 text-sm text-ink/60 font-mono"
              data-testid="suggestions-loading"
            >
              <Loader2 size={14} className="animate-spin" />
              Loading suggestions…
            </div>
          )}

          {!loading && error && (
            <div
              className="flex items-start gap-2 p-3 rounded-sm border border-shelf-urgent bg-shelf-urgent-bg text-shelf-urgent text-sm"
              data-testid="suggestions-error"
            >
              <AlertTriangle size={16} className="mt-0.5" />
              <div>
                <div className="font-display font-black uppercase tracking-wider text-xs">
                  Could not load suggestions
                </div>
                <div className="opacity-80 mt-0.5">{error}</div>
              </div>
            </div>
          )}

          {!loading && !error && data && cards.length === 0 && (
            <div
              className="text-sm text-ink/60"
              data-testid="suggestions-empty"
            >
              <p className="font-semibold text-ink">{emptyCopy.title}</p>
              <p className="mt-1">{emptyCopy.message}</p>
            </div>
          )}

          {!loading && !error && data === null && (
            <div
              className="text-sm text-ink/60"
              data-testid="suggestions-unavailable"
            >
              Suggestions are not available right now.
            </div>
          )}

          {!loading && !error && cards.map((card, i) => (
            <SuggestionCard key={`sg-${i}`} card={card} index={i} />
          ))}
        </div>

        <div className="px-5 py-3 border-t border-border flex items-center gap-2 bg-secondary/30">
          <button
            type="button"
            onClick={handleRefresh}
            disabled={loading}
            className="h-10 px-3 border border-ink/40 bg-paper text-ink font-display text-[11px] uppercase tracking-wider rounded-sm hover:bg-secondary disabled:opacity-50 flex items-center gap-1.5"
            data-testid="suggestions-refresh-btn"
          >
            <RefreshCw size={12} strokeWidth={2.5} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
          <button
            type="button"
            onClick={handleGotIt}
            className="flex-1 h-10 px-3 bg-ink text-paper font-display text-[11px] uppercase tracking-wider rounded-sm hover:bg-brand transition-colors flex items-center justify-center gap-1.5"
            data-testid="suggestions-got-it-btn"
          >
            <Check size={14} strokeWidth={2.5} />
            Got it
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default SuggestionsDrawer;
